import React, { useMemo, useState } from 'react';
import { Avatar, Box, Chip, IconButton, InputAdornment, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { FixedSizeList } from 'react-window';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ROLE_DEFINITIONS } from '../../../../services/roleService';
import { VIEW_AS_ROLE_ORDER, compareByLastName, filterMembers, memberUnits } from '../../../../services/viewAs';
import { memberUnitLabel, unitOptions } from './memberForm';

export const ROLE_COLORS = {
  org_admin: '#d32f2f',
  org_it: '#455a64',
  executive: '#0d47a1',
  site_manager: '#1976d2',
  department_lead: '#7b1fa2',
  team_lead: '#f57c00',
  respondent: '#388e3c',
};

const ROW = 56;

function initials(m) {
  const name = m.displayName || m.email || '';
  const parts = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * Members list (Beta-1 plan P4-10, TICK A38): sorted by last name, grouped by role in Tim's order,
 * search + filters by role and site / department / NWT, virtualized rows (react-window) with
 * "showing N of M" so 1,000+ members stay smooth. Rows show title and unit.
 */
export function MembersList({ members, organization, onEdit, onRemove, disabled = false, height = 520 }) {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState(''); // 'site:ID' | 'department:ID' | 'team:ID'
  const opts = useMemo(() => unitOptions(organization), [organization]);

  const rows = useMemo(() => {
    let list = filterMembers(members, query);
    if (roleFilter) list = list.filter((m) => m.role === roleFilter);
    if (unitFilter) {
      const [type, id] = unitFilter.split(':');
      list = list.filter((m) => {
        const u = memberUnits(m, organization);
        const all = Array.isArray(m.scopeIds) ? m.scopeIds : [];
        if (type === 'site') return u.siteId === id || all.includes(id);
        if (type === 'department') return u.deptId === id || all.includes(id);
        if (type === 'team') return u.nwtId === id || all.includes(id);
        return true;
      });
    }
    const out = [];
    for (const role of VIEW_AS_ROLE_ORDER) {
      const group = list.filter((m) => m.role === role).sort(compareByLastName);
      if (!group.length) continue;
      out.push({ kind: 'header', role, count: group.length });
      group.forEach((m) => out.push({ kind: 'member', member: m }));
    }
    const unknown = list.filter((m) => !VIEW_AS_ROLE_ORDER.includes(m.role)).sort(compareByLastName);
    if (unknown.length) {
      out.push({ kind: 'header', role: 'other', count: unknown.length });
      unknown.forEach((m) => out.push({ kind: 'member', member: m }));
    }
    return out;
  }, [members, query, roleFilter, unitFilter, organization]);

  const shown = rows.filter((r) => r.kind === 'member').length;

  const Row = ({ index, style }) => {
    const r = rows[index];
    if (r.kind === 'header') {
      return (
        <Box style={style} sx={{ display: 'flex', alignItems: 'center', px: 1, bgcolor: 'action.hover' }}>
          <Typography variant="overline" sx={{ color: ROLE_COLORS[r.role] || 'text.secondary', fontWeight: 700 }}>
            {ROLE_DEFINITIONS[r.role]?.label || 'Other'}
          </Typography>
          <Chip label={r.count} size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
        </Box>
      );
    }
    const m = r.member;
    const unit = memberUnitLabel(m, organization);
    return (
      <Box style={style} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Avatar src={m.photoURL || undefined} sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: `${ROLE_COLORS[m.role] || '#757575'}` }}>{initials(m)}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {m.displayName || m.email || m.uid}
            {m.title ? <Typography component="span" variant="caption" color="text.secondary"> · {m.title}</Typography> : null}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {[m.email, unit].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {Array.isArray(m.scopeIds) && m.scopeIds.length > 1 && <Chip label={`${m.scopeIds.length} units`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
        <Tooltip title="Edit member">
          <span>
            <IconButton size="small" onClick={() => onEdit(m)} disabled={disabled} aria-label={`Edit ${m.displayName || m.email || m.uid}`}><EditIcon fontSize="small" /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove from organization">
          <span>
            <IconButton size="small" onClick={() => onRemove(m)} disabled={disabled} aria-label={`Remove ${m.displayName || m.email || m.uid}`}><DeleteIcon fontSize="small" /></IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small" fullWidth placeholder="Search by name, email or title" value={query} onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          inputProps={{ 'aria-label': 'Search members' }}
        />
        <TextField size="small" select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">All roles</MenuItem>
          {VIEW_AS_ROLE_ORDER.map((r) => <MenuItem key={r} value={r}>{ROLE_DEFINITIONS[r]?.label || r}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Site / Department / NWT" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)} sx={{ minWidth: 260 }}>
          <MenuItem value="">Everywhere</MenuItem>
          {opts.sites.map((s) => <MenuItem key={`site:${s.id}`} value={`site:${s.id}`}>Site · {s.label}</MenuItem>)}
          {opts.departments.map((d) => <MenuItem key={`department:${d.id}`} value={`department:${d.id}`}>Dept · {d.label}</MenuItem>)}
          {opts.teams.map((t) => <MenuItem key={`team:${t.id}`} value={`team:${t.id}`}>NWT · {t.label}</MenuItem>)}
        </TextField>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Showing {shown} of {members.length} member{members.length === 1 ? '' : 's'}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {members.length === 0 ? 'No members yet. Add people below.' : 'No members match these filters.'}
        </Typography>
      ) : (
        <FixedSizeList height={Math.min(height, rows.length * ROW)} itemCount={rows.length} itemSize={ROW} width="100%">
          {Row}
        </FixedSizeList>
      )}
    </Box>
  );
}

export default MembersList;
